import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarCheck, Pencil, Check, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { SelectField, useAssignees } from "@/lib/crm";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => { const d = iso ? new Date(iso) : new Date(Date.now() + 86400000); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };

const Planned = ({ items }) => (
  <ul className="mt-2 space-y-1.5" data-testid="next-action-planned">
    {items.map((p) => (
      <li key={p.id}><Link to={p.link} className="group flex items-center gap-2 text-sm" data-testid={`next-action-planned-${p.id}`}>
        {p.kind === "intervention" ? <Wrench className="h-3.5 w-3.5 text-brand-600" /> : <CalendarCheck className="h-3.5 w-3.5 text-brand-600" />}
        <span className="font-medium text-ink group-hover:underline">{p.title}</span><span className="text-slate-500">· {fmtDateTime(p.at)}</span>
      </Link></li>))}
  </ul>
);

export const NextActionPanel = ({ action, endpoint, canEdit, onChanged, defaultOwner, completeEndpoint, types, planned = [] }) => {
  const users = useAssignees();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const completing = open === "complete";
  useEffect(() => {
    if (open === "complete") setF({ title: "", due: toLocal(null), owner_id: action?.owner_id || defaultOwner, type: "follow_up", note: "" });
    else if (open) setF({ title: action?.title || "", due: toLocal(action?.due_at), owner_id: action?.owner_id || defaultOwner, type: action?.type || "follow_up" });
  }, [open, action, defaultOwner]);
  const payload = () => ({ title: f.title, due_at: new Date(f.due).toISOString(), owner_id: f.owner_id, ...(types ? { type: f.type } : {}) });
  const complete = async (withNext) => {
    try { await api.post(completeEndpoint, { note: f.note || null, next: withNext ? payload() : null }); toast.success(withNext ? "Azione completata e successiva pianificata" : "Azione completata"); setOpen(false); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const overdue = action && new Date(action.due_at) < new Date();

  const save = async () => {
    if (completing) return complete(true);
    try { await api.put(endpoint, payload()); toast.success("Prossima azione salvata"); setOpen(false); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const done = async () => {
    try { await api.delete(endpoint); toast.success("Prossima azione completata"); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <Panel className={`p-5 ${!action && !planned.length ? "border-amber-200" : overdue ? "border-red-200" : ""}`} data-testid="next-action-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><CalendarClock className="h-4 w-4" /></span>
          <div>
            <SectionLabel>Prossima azione</SectionLabel>
            {action ? <>
              <p className="mt-1 text-base font-semibold text-ink" data-testid="next-action-title">{action.title}</p>
              <p className={`text-sm ${overdue ? "font-semibold text-red-600" : "text-slate-600"}`}>{fmtDateTime(action.due_at)}{overdue && " · scaduta"}</p>
              {types && action.type && <p className="text-xs text-slate-500">{types.find((t) => t.key === action.type)?.label}</p>}
              <p className="text-xs text-slate-500">Responsabile: {action.owner_name || users.find((u) => u.id === action.owner_id)?.name || "—"}</p>
            </> : !planned.length && <p className="mt-1 text-sm font-medium text-amber-700" data-testid="next-action-empty">Nessuna prossima azione pianificata</p>}
            {planned.length > 0 && <>{action && <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Pianificati</p>}<Planned items={planned} /></>}
          </div>
        </div>
        {canEdit && <div className="flex gap-1">
          {action && completeEndpoint && <Button size="sm" variant="outline" onClick={() => setOpen("complete")} className="gap-1" data-testid="next-action-complete"><Check />Fatto</Button>}
          {action && !completeEndpoint && endpoint.includes("/companies/") && <Button size="sm" variant="outline" onClick={done} className="gap-1" data-testid="next-action-done"><Check />Fatto</Button>}
          <Button size="sm" variant={action ? "ghost" : "default"} onClick={() => setOpen(true)} className="gap-1" data-testid="next-action-edit">{action ? <Pencil /> : null}{action ? "" : "Pianifica"}</Button>
        </div>}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => setOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="next-action-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{completing ? "Azione completata" : "Prossima azione"}</DialogTitle>
            <DialogDescription>{completing ? `"${action?.title}" sarà registrata come completata. Pianifica subito la successiva.` : "Cosa deve succedere, quando e chi se ne occupa."}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            {completing && <div className="space-y-1.5"><Label className="text-xs">Esito (facoltativo)</Label><Input maxLength={500} value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="next-action-input-note" /></div>}
            {types && <SelectField label="Tipo" value={f.type} onChange={(v) => setF({ ...f, type: v })} options={types} testId="next-action-input-type" />}
            <div className="space-y-1.5"><Label className="text-xs">Azione</Label><Input maxLength={200} placeholder="Es. Richiamare il cliente" value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="next-action-input-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Data e ora</Label><Input type="datetime-local" value={f.due || ""} onChange={(e) => setF({ ...f, due: e.target.value })} data-testid="next-action-input-due" /></div>
            <SelectField label="Responsabile" value={f.owner_id} onChange={(v) => setF({ ...f, owner_id: v })} options={users} testId="next-action-input-owner" />
          </div>
          <DialogFooter className="gap-2">
            {completing ? <Button variant="outline" onClick={() => complete(false)} data-testid="next-action-complete-only">Completa senza successiva</Button>
              : <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>}
            <Button onClick={save} disabled={!f.title?.trim() || !f.due} data-testid="next-action-save">{completing ? "Completa e pianifica" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};
